import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { http, API } from "@/lib/api";
import Seo from "@/components/Seo";
import { useI18n, pickLocalized } from "@/lib/i18n";
import { Download, FileText } from "lucide-react";

export default function DataPage() {
  const { locale } = useI18n();
  const [reports, setReports] = useState([]);
  const [sectors, setSectors] = useState([]);
  useEffect(() => {
    http.get("/sector-reports").then((r) => setReports(r.data.reports || []));
    http.get("/sectors").then((r) => setSectors(r.data.sectors || []));
  }, []);
  return (
    <>
      <Seo title="Data & methodology" description="Public factual aggregates and sector reports." canonicalPath="/data" locale={locale} />
      <section className="border-b" style={{ borderColor: "var(--bp-border)" }}>
        <div className="bp-container py-14 max-w-4xl">
          <div className="uppercase text-xs tracking-[0.3em] mb-3" style={{ color: "var(--bp-text-muted)" }}>Press & data</div>
          <h1 className="font-serif text-5xl md:text-6xl leading-none">Data &amp; methodology</h1>
          <p className="mt-6 text-lg" style={{ color: "var(--bp-text-secondary)" }}>
            BrandPassport is a public index of brand transparency. All data on this site is derived from publicly available sources and reviewed by editors before publication. This page lists every sector report we publish and offers a factual aggregates export in CSV format.
          </p>
        </div>
      </section>

      <div className="bp-container py-12 max-w-4xl space-y-12">
        <section>
          <h2 className="font-serif text-3xl mb-4">Methodology</h2>
          <div className="space-y-4 text-base leading-relaxed" style={{ color: "var(--bp-text-secondary)" }}>
            <p>Each brand profile is built by researching seven categories aligned to the EU Digital Product Passport: product identity, materials, origin, repair &amp; care, end of life, environmental footprint, and certifications.</p>
            <p>For each category, editors assign one of three statuses — <b>Found</b>, <b>Partial</b>, or <b>Not found in public sources</b> — anchored to a source URL and retrieval date. <b>We do not compute scores, grades, rankings, or percentages that could be read as a rating.</b> The DPP readiness note reports a factual count only.</p>
            <p>All changes are recorded in a per-profile changelog. Sector reports summarise counts and quarter-over-quarter changes across all published profiles in a sector.</p>
          </div>
        </section>

        <section>
          <h2 className="font-serif text-3xl mb-4">Sector reports</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {reports.map((r) => (
              <Link key={r.slug} to={`/reports/${r.slug}`} className="block p-5 bg-white border hover:border-black transition-colors" style={{ borderColor: "var(--bp-border)" }} data-testid={`data-report-${r.slug}`}>
                <div className="uppercase text-xs tracking-widest" style={{ color: "var(--bp-text-muted)" }}>{r.sector_code} · {r.quarter}</div>
                <div className="font-serif text-2xl mt-1">{r.sector_code === "FASHION" ? "Fashion" : r.sector_code === "FURNITURE" ? "Furniture" : r.sector_code} sector report</div>
                <div className="mt-2 text-xs font-mono" style={{ color: "var(--bp-text-muted)" }}>Published {(r.approved_at || r.generated_at || "").slice(0, 10)}</div>
              </Link>
            ))}
            {reports.length === 0 && <p style={{ color: "var(--bp-text-muted)" }}>No sector reports published yet.</p>}
          </div>
        </section>

        <section>
          <h2 className="font-serif text-3xl mb-4">CSV export — sector aggregates</h2>
          <p className="mb-3" style={{ color: "var(--bp-text-secondary)" }}>
            Download factual aggregate counts per sector, per category. <b>Per-brand data is not exported</b> — only sector-level counts are available in bulk. This preserves editorial oversight and respects our neutrality.
          </p>
          <a href={`${API}/data/aggregates.csv`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-4 py-2 border border-black text-sm" data-testid="csv-download"><Download size={14} /> Download aggregates.csv</a>
          <p className="mt-4 text-sm italic" style={{ color: "var(--bp-text-muted)" }}>
            <b>Attribution required</b>: any re-use of this data must credit &ldquo;BrandPassport (brandpassport.ai)&rdquo; and link to this page.
          </p>
        </section>

        <section>
          <h2 className="font-serif text-3xl mb-4">Press contact</h2>
          <p style={{ color: "var(--bp-text-secondary)" }}>
            Journalists: reach us at press@brandpassport.ai. We respond within 2 business days. We do not comment on brands individually — please refer to the public profile and cited sources.
          </p>
        </section>
      </div>
    </>
  );
}
